import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {cloneDeep} from 'lodash-es';
import {Subscription} from 'rxjs';
import {NavigationEnd, Router} from '@angular/router';
import {RouteService} from '../../../../services/route/route.service';
import {AdminService, TranslationsSearch} from '../../../../services/admin/admin.service';
import {StorageService} from '../../../../services/storage/storage.service';
import {LanguageService} from '../../../../services/language/language.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {MatDialog} from '@angular/material/dialog';
import {TemplatesViewComponent} from '../view/templates-view.component';
import {ConfirmDialogComponent} from '../../../../components/confirm-dialog/confirm-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {finalize} from 'rxjs/operators';
import {TranslationsPage} from '../../../../generated/translation-client';
import {TranslationsDialogData} from '../../translations/view/translations-view.component';
import {DialogService} from '../../../../services/dialog/dialog.service';
import moment from "moment/moment";
import {UtilService} from "../../../../services/util/util.service";

interface TemplatesListFilter {
  key?: string;
  word?: string;
  startDate?: string;
  endDate?: string;
  untranslatedLanguage?: string;
}

@Component({
  selector: 'app-list',
  templateUrl: './templates-list.component.html',
  styleUrls: ['./templates-list.component.scss']
})
export class TemplatesListComponent implements OnInit, AfterViewInit, OnDestroy {
  static STORAGE_SEARCH_RESULTS_PAGE_SIZE = 'templatesResults.pageSize';
  static STORAGE_SEARCH_RESULTS_PAGE = 'templatesResults.page';
  static STORAGE_SEARCH_RESULTS_SELECTED_LANGUAGES = 'templatesResults.selectedLanguages';
  private _subscriptions: Subscription[] = [];
  private _defaultFilters: TemplatesListFilter = {
    key: null,
    word: null,
    startDate: null,
    endDate: null,
    untranslatedLanguage: null,
  };
  public filters: TemplatesListFilter = cloneDeep(this._defaultFilters);
  results: TranslationsPage = { items: {}, total: 0 };
  pageSizeOptions: number[] = [30, 60, 90];
  pageSize: number = this.pageSizeOptions[0];
  page = 1;
  isLoading = false;
  private coldStart = false;
  public selectedLanguages: string[] = [];
  public sortedLanguages: string[] = [];
  public showMobileFilters: boolean = false;
  maxDate: string = moment().utc().toISOString();

  constructor(public languageService: LanguageService,
              private router: Router,
              private routeService: RouteService,
              private adminService: AdminService,
              private storageService: StorageService,
              private authService: AuthService,
              private dialog: MatDialog,
              private dialogService: DialogService,
              private translateService: TranslateService,
              public utilService: UtilService) {
    if (storageService.get(TemplatesListComponent.STORAGE_SEARCH_RESULTS_PAGE_SIZE)) {
      this.pageSize = parseInt(storageService.get(TemplatesListComponent.STORAGE_SEARCH_RESULTS_PAGE_SIZE), 0);
    }
    if (storageService.get(TemplatesListComponent.STORAGE_SEARCH_RESULTS_PAGE)) {
      this.page = parseInt(storageService.get(TemplatesListComponent.STORAGE_SEARCH_RESULTS_PAGE), 0);
    }
    if (storageService.get(TemplatesListComponent.STORAGE_SEARCH_RESULTS_SELECTED_LANGUAGES)) {
      this.selectedLanguages = JSON.parse(storageService.get(TemplatesListComponent.STORAGE_SEARCH_RESULTS_SELECTED_LANGUAGES));
      if (this.selectedLanguages.length) {
        this.languageService.sortLanguages(this.selectedLanguages, this.authService.salesPointLanguage);
      }
    }
  }

  public ngOnInit(): void {
    this.coldStart = true;
    this.languageService.getLanguages().subscribe((languages) =>
      this.sortedLanguages = this.languageService.sortLanguages(languages, this.authService.salesPointLanguage));
    if (!this.selectedLanguages.length) {
      this.setDefaults();
    }
    this.filters = cloneDeep(this._defaultFilters);
    this.search();
  }
  public ngAfterViewInit(): void {
    if (this.routeService.checkRoute(['admin', 'templates', 'create'])) {
      this.create();
    }
    if (this.routeService.checkRoute(['admin', 'templates', 'edit'])) {
      this.edit(this.routeService.params['key']);
    }
    this._subscriptions.push(this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        if (e.urlAfterRedirects === '/app/admin/templates') {
          if (!this.coldStart) {
            if (this.routeService.url === this.routeService.prevUrl) {
              this.clearFilters(true);
            }
            this.search(this.page);
          } else {
            this.coldStart = false;
          }
        }
      }
    }));
  }
  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  setDefaults(): void {
    this.selectedLanguages = [];
    this.selectedLanguages.push('en');
    if (this.authService.salesPointLanguage) {
      this.selectedLanguages.push(this.authService.salesPointLanguage);
    }
    this.updateLocalStorage();
  }

  clearFilters(useDefaults: boolean = false): void {
    const filters = cloneDeep(this._defaultFilters);
    if (!useDefaults) {
      Object.keys(filters).forEach((key) => {
        (filters as any)[key] = null;
      });
    }
    this.filters = filters;
    this.setDefaults();
  }

  get resultsKeys(): string[] {
    return Object.keys(this.results.items);
  }

  get isFormValid(): boolean {
    let check = true;
    if (!check) {
      let notEmpty = false;
      Object.keys(this.filters).forEach((key) => {
        if ((this.filters as any)[key]) {
          notEmpty = true;
        }
      });
      check = notEmpty;
    }
    return check;
  }

  public search(page = 1): void {
    this.showMobileFilters = false;
    this.isLoading = true;
    this.results = { items: {}, total: 0 };
    this.page = page;
    const filters = cloneDeep(this.filters);
    const data: TranslationsSearch = {
      key: filters.key,
      word: filters.word,
      startDate: filters.startDate ? moment(filters.startDate).format('YYYY-MM-DD') : null,
      endDate: filters.endDate ? moment(filters.endDate).format('YYYY-MM-DD') : null,
      page: this.page,
      pageSize: this.pageSize,
      isCentreBased: true,
      untranslatedLanguage: filters.untranslatedLanguage
    };
    if (data.key?.length) {
      data.key = data.key.trim();
    }
    if (data.word?.length) {
      data.word = data.word.trim();
    }
    this.adminService.searchTranslations(data).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((results) => this.results = results);
  }

  public toggleFilters(): void {
    this.showMobileFilters = !this.showMobileFilters;
  }

  updateLocalStorage(): void {
    this.storageService.set(TemplatesListComponent.STORAGE_SEARCH_RESULTS_PAGE_SIZE, this.pageSize);
    this.storageService.set(TemplatesListComponent.STORAGE_SEARCH_RESULTS_SELECTED_LANGUAGES, JSON.stringify(this.selectedLanguages));
  }

  create(): void {
    this.routeService.replaceUrl('/app/admin/templates/create');
    this.openDialog();
  }

  edit(key: string): void {
    this.routeService.replaceUrl('/app/admin/templates/edit/' + key);
    this.openDialog({ key: key });
  }

  view(key: string): void {
    this.routeService.replaceUrl('/app/admin/templates/view/' + key);
    this.openDialog({ key: key });
  }

  confirmDelete(key: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('general.api_templates_delete_title'),
        description: this.translateService.instant('general.api_templates_delete_description'),
        buttonCancel: this.translateService.instant('general.api_general_cancel'),
        buttonOk: this.translateService.instant('general.api_templates_delete')
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.delete(key);
      }
    });
  }
  public delete(key: string): void {
    this.isLoading = true;
    this.adminService.deleteTranslation(key, true).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(() => this.search());
  }

  openDialog(data: TranslationsDialogData = {}): void {
    this.dialogService.open(TemplatesViewComponent, {
      maxHeight: '100%',
      minWidth: '60%',
      maxWidth: '80%',
      data: data
    });
  }
}
